import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO, { buildCanonicalUrl } from '../components/SEO';
import { domainToolLinks } from '../config/navigation';
import { isValidDomain, normalizeDomainInput } from '../utils/domainInput';

const ArrowIcon = ({ className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const auditChecks = [
  {
    to: '/audit/',
    title: 'Exposed secret patterns',
    description: 'Review public JavaScript for credential-like keys and tokens.'
  },
  {
    to: '/audit/',
    title: 'Security headers',
    description: 'Check common browser protections returned by the website.'
  },
  {
    to: '/ssl-checker/',
    title: 'SSL certificate',
    description: 'Inspect certificate validity, host coverage, issuer, and expiry.'
  },
  {
    to: '/dns-health/',
    title: 'DNS and email',
    description: 'Review core records plus common SPF, DKIM, and DMARC signals.'
  },
  {
    to: '/audit/',
    title: 'Blacklist reputation',
    description: 'Collect point-in-time responses from configured threat providers.'
  },
  {
    to: '/audit/',
    title: 'Third-party scripts',
    description: 'See the external scripts declared by the homepage.'
  }
];

const heroToolLinks = [
  ...domainToolLinks,
  {
    to: '/search-visibility/',
    title: 'Search & AI Visibility'
  }
];

const sampleBadgeStyles = {
  good: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  review: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  measured: 'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300'
};

const sampleVisibilityRows = [
  { title: 'Googlebot', subtitle: 'Google Search, including AI Overviews', badge: 'Allowed', tone: 'good' },
  { title: 'GPTBot', subtitle: 'OpenAI model training', badge: 'Blocked', tone: 'review' },
  { title: 'Readable without JavaScript', subtitle: 'Initial homepage text counted', badge: 'Measured', tone: 'measured' }
];

const sampleAuditRows = [
  { title: 'SSL certificate', subtitle: 'Valid, with full host coverage', badge: 'A+', tone: 'good' },
  { title: 'Security headers', subtitle: 'Some browser protections missing', badge: 'B', tone: 'review' },
  { title: 'Exposed secret patterns', subtitle: 'None found in scanned files', badge: 'A+', tone: 'good' }
];

const SampleCard = ({ label, headline, rows }) => (
  <figure className="rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <figcaption className="flex items-center justify-between gap-3">
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
        {label}
      </span>
      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{headline}</span>
    </figcaption>
    <ul className="mt-4 space-y-3">
      {rows.map((row) => (
        <li key={row.title} className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{row.title}</p>
            <p className="text-xs leading-5 text-gray-600 dark:text-gray-300">{row.subtitle}</p>
          </div>
          <span
            className={`rounded-md border px-1.5 py-0.5 font-mono text-xs font-bold whitespace-nowrap ${sampleBadgeStyles[row.tone]}`}
          >
            {row.badge}
          </span>
        </li>
      ))}
    </ul>
  </figure>
);

const SampleReportStrip = () => (
  <div className="mx-auto mt-10 max-w-4xl">
    <p className="mb-3 text-center font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
      Sample reports for yourdomain.com
    </p>
    <div className="grid gap-4 sm:grid-cols-2">
      <SampleCard label="AI visibility" headline="robots.txt + homepage HTML" rows={sampleVisibilityRows} />
      <SampleCard label="Security audit" headline="Overall 78/100" rows={sampleAuditRows} />
    </div>
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const domainInputRef = useRef(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');

  const goToTool = (path) => {
    const cleanDomain = normalizeDomainInput(domainInput);

    if (!isValidDomain(cleanDomain)) {
      setDomainError('Enter a complete domain such as example.com.');
      domainInputRef.current?.focus();
      return;
    }

    setDomainError('');
    navigate(`${path}?domain=${encodeURIComponent(cleanDomain)}`);
  };

  const handleScan = (event) => {
    event.preventDefault();
    goToTool('/search-visibility/');
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://domainoptic.com';
  const softwareJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'DomainOptic',
    description: 'Review the public signals that can affect search and AI access, run a passive security audit, research domain availability, and generate meaningful domain names.',
    url: buildCanonicalUrl('/'),
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      name: 'Initial audit',
      price: '0',
      priceCurrency: 'USD'
    },
    datePublished: '2025-01-01',
    dateModified: '2026-07-23',
    featureList: [
      'Website Security Audit',
      'Public JavaScript Secret Pattern Scanner',
      'Third-Party Script Inventory',
      'SSL Certificate Checker',
      'DNS Health Check',
      'Security Headers Analysis',
      'Reputation Signals',
      'Search and AI Visibility Signals',
      'AI Domain Generator',
      'Domain Availability and Registration Data',
      'Premium Domain Guide and Live Checker',
      'Domain Comparison'
    ]
  };

  const guideLinks = [
    {
      to: '/blog/prompt-injection-defense-checklist-2026/',
      title: 'Prompt Injection Defense Checklist',
      description: 'Practical guardrails for system prompts, tool routing, and high-risk inputs.'
    },
    {
      to: '/blog/ai-agent-security-audit-checklist-2026/',
      title: 'AI Agent Security Audit Checklist',
      description: 'Review identity, permissions, secrets, and execution boundaries before launch.'
    },
    {
      to: '/blog/secure-llm-api-key-management-2026/',
      title: 'Secure LLM API Key Management',
      description: 'Understand client-side limits, server-side storage, rotation, and blast radius.'
    }
  ];

  return (
    <div>
      <SEO
        title="DomainOptic | AI Visibility, Security Audit, and Domain Tools"
        description="Review the public signals that can affect search and AI access, run a passive security audit, check domain availability, and generate meaningful domain names."
        canonicalPath="/"
        type="website"
        ogImage={`${siteUrl}/og-domainoptic-audit-domain-tools-2026.png?v=20260720`}
        jsonLd={softwareJsonLd}
      />

      <section className="border-b border-gray-200 bg-gray-50 text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-heading text-4xl font-extrabold tracking-[-0.035em] text-gray-900 dark:text-white sm:text-5xl lg:text-[3rem] lg:leading-[1.1]">
              Seen by search and AI? Secure?{' '}
              <br aria-hidden="true" className="hidden sm:block" />
              Find your next domain.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-300 sm:text-xl">
              See what search engines and AI assistants can read on your site, run a read-only security audit, and research your next domain.
            </p>

            <form onSubmit={handleScan} noValidate className="mx-auto mt-8 max-w-xl" aria-label="Check a website">
                <label htmlFor="homepage-domain" className="sr-only">
                  Website domain
                </label>
                <input
                  ref={domainInputRef}
                  id="homepage-domain"
                  name="domain"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={domainInput}
                  onChange={(event) => {
                    setDomainInput(event.target.value);
                    if (domainError) setDomainError('');
                  }}
                  placeholder="example.com"
                  aria-invalid={Boolean(domainError)}
                  aria-describedby={domainError ? 'homepage-domain-error' : undefined}
                  className={`ui-input ${domainError ? 'ui-input-error' : ''}`}
                />
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <button type="submit" className="ui-button-primary inline-flex min-h-12 flex-1 justify-center px-6 text-base">
                    Check AI visibility
                  </button>
                  <button
                    type="button"
                    onClick={() => goToTool('/audit/')}
                    className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border-2 border-emerald-700 px-6 text-base font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 motion-reduce:transition-none dark:border-emerald-400/60 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                  >
                    Run security audit
                  </button>
                </div>
                {domainError && (
                  <p id="homepage-domain-error" role="alert" className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
                    <svg aria-hidden="true" className="h-4 w-4 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {domainError}
                  </p>
                )}
              </form>

              <nav aria-label="Domain and visibility tools" className="mx-auto mt-7 flex max-w-xl flex-wrap justify-center gap-2">
                {heroToolLinks.map((tool) => (
                  <Link
                    key={tool.to}
                    to={tool.to}
                    className="inline-flex min-h-10 items-center rounded-full border border-gray-300 px-3.5 text-[13px] font-semibold text-gray-700 transition-colors hover:border-emerald-600 hover:text-emerald-700 motion-reduce:transition-none dark:border-gray-700 dark:text-gray-200 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
                  >
                    {tool.title}
                  </Link>
                ))}
              </nav>
          </div>

          <SampleReportStrip />
        </div>
      </section>

      <section className="border-b border-gray-200 bg-gray-50 py-14 text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-white sm:py-16" aria-labelledby="checks-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
            <div className="max-w-md">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">One report</p>
              <h2 id="checks-heading" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                Six checks. One clearer picture.
              </h2>
              <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
                DomainOptic stays on the public surface and explains what it observes without active exploitation or credential testing.
              </p>
              <Link
                to="/scanner-info/"
                className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-100 motion-reduce:transition-none dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
              >
                How scanning works
                <ArrowIcon />
              </Link>
            </div>

            <ol className="grid border-t border-gray-200 dark:border-gray-800 sm:grid-cols-2">
              {auditChecks.map((check, index) => (
                <li
                  key={check.title}
                  className={`border-b border-gray-200 dark:border-gray-800 ${index % 2 === 1 ? 'sm:border-l sm:pl-6' : 'sm:pr-6'}`}
                >
                  <Link to={check.to} className="group grid min-h-[132px] grid-cols-[auto_1fr] gap-4 py-5">
                    <span className="font-mono text-xs font-semibold tracking-[0.12em] text-emerald-700 dark:text-emerald-300">{String(index + 1).padStart(2, '0')}</span>
                    <span>
                      <span className="flex items-center justify-between gap-4 font-semibold text-gray-900 group-hover:text-emerald-700 dark:text-white dark:group-hover:text-emerald-300">
                        {check.title}
                        <ArrowIcon className="h-3.5 w-3.5 flex-none transition-transform group-hover:translate-x-1 motion-reduce:transition-none" />
                      </span>
                      <span className="mt-2 block text-sm leading-6 text-gray-600 dark:text-gray-300">{check.description}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-10 grid gap-4 border-y border-gray-200 py-6 dark:border-gray-800 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-6">
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Separate check</span>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Search and AI Visibility</h3>
              <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">See whether search engines can include your homepage and understand its basic information.</p>
            </div>
            <Link
              to="/search-visibility/"
              className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-gray-900 dark:text-emerald-300 dark:hover:text-white"
            >
              Check search visibility
              <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-gray-200 bg-gray-50 py-16 text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-white sm:py-20" aria-labelledby="domains-heading">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16 lg:px-8">
          <div className="max-w-md">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Domain workbench</p>
            <h2 id="domains-heading" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Find the name for what comes next.
            </h2>
            <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
              Start with an idea, check point-in-time registration data, browse memorable options, and compare your finalists.
            </p>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-800">
            {domainToolLinks.map((tool, index) => (
              <Link
                key={tool.to}
                to={tool.to}
                className="group grid grid-cols-[auto_1fr_auto] gap-4 border-b border-gray-200 py-6 dark:border-gray-800 sm:gap-6"
              >
                <span className="font-mono text-xs font-semibold tracking-[0.12em] text-emerald-700 dark:text-emerald-300">{String(index + 1).padStart(2, '0')}</span>
                <span>
                  <span className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900 group-hover:text-emerald-700 dark:text-white dark:group-hover:text-emerald-300">
                    {tool.title}
                    {index === 0 && (
                      <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        Popular
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-sm leading-6 text-gray-600 dark:text-gray-300">{tool.description}</span>
                </span>
                <ArrowIcon className="mt-1 h-4 w-4 flex-none text-emerald-700 transition-transform group-hover:translate-x-1 motion-reduce:transition-none dark:text-emerald-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-16 text-gray-900 dark:bg-gray-900 dark:text-white sm:py-20" aria-labelledby="guides-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16 lg:items-start">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Current field guides</p>
              <h2 id="guides-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Security guidance for AI-era apps.</h2>
              <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
                Practical 2026 checklists for risks a passive website scan cannot fully evaluate.
              </p>
              <Link
                to="/blog/"
                className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-100 motion-reduce:transition-none dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
              >
                Browse all guides
                <ArrowIcon />
              </Link>
            </div>

            <div className="divide-y divide-gray-200 border-y border-gray-200 dark:divide-gray-800 dark:border-gray-800">
              {guideLinks.map((guide) => (
                <Link key={guide.to} to={guide.to} className="group flex items-center justify-between gap-5 py-5">
                  <span>
                    <span className="block font-semibold text-gray-900 group-hover:text-emerald-700 dark:text-white dark:group-hover:text-emerald-300">{guide.title}</span>
                    <span className="mt-1 block text-sm leading-6 text-gray-600 dark:text-gray-300">{guide.description}</span>
                  </span>
                  <span className="flex-none text-emerald-700 transition-transform group-hover:translate-x-1 motion-reduce:transition-none dark:text-emerald-300">
                    <ArrowIcon />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-16 grid gap-8 border-t border-gray-200 pt-12 dark:border-gray-800 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16" aria-labelledby="passion-project-heading">
            <div>
              <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-300">
                No signup to start
              </span>
              <h2 id="passion-project-heading" className="mt-5 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                Built for builders.
              </h2>
            </div>
            <div>
              <p className="max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-300">
                Use the audit and domain tools without a checkout flow. The goal is simple: make public website signals and domain decisions easier to understand.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link to="/audit/" className="ui-button-primary inline-flex justify-center px-6 text-base">
                  Run an audit
                  <ArrowIcon />
                </Link>
                <Link
                  to="/generator/"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-gray-300 px-6 py-2.5 text-base font-semibold text-gray-900 transition-colors hover:bg-gray-100 motion-reduce:transition-none dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
                >
                  Generate domain ideas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
